import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
	ThemeEvents,
	CartUpdateEvent,
	QuantitySelectorUpdateEvent,
	CartAddEvent,
	DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
	#debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

	connectedCallback() {
		super.connectedCallback();

		document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
		document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
		document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

		this.addEventListener('click', this.#handleSubscriptionButton);
		this.addEventListener('change', this.#handleSubscriptionChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
		document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
	}

	/**
	 * Handles QuantitySelectorUpdateEvent change event.
	 * @param {QuantitySelectorUpdateEvent} event - The event.
	 */
	async #onQuantityChange(event) {
		const { quantity, cartLine: line } = event.detail;

		if (!line) return;

		if (quantity === 0) {
			return this.onLineItemRemove(line);
		}

		const linked = await this.#findLinkedLine(line);

		if (linked) {
			this.#applyLineQuantities(linked.items, { [line]: quantity, [linked.line]: quantity });
		} else {
			this.updateQuantity({ line, quantity, action: 'change' });
		}

		const lineItemRow = this.refs.cartItemRows[line - 1];

		if (!lineItemRow) return;

		const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
		textComponent?.shimmer();
	}

	#handleSubscriptionButton = (event) => {
		const button = event.target.closest('[data-cart-subscription-upsell]');

		if (!button) return;

		const wrapper = button.closest('[data-cart-subscription-wrapper]');
		const selectWrapper = wrapper?.querySelector('[data-cart-subscription-plans]');
		const select = wrapper?.querySelector('select');

		if (!wrapper || !select) return;

		const firstSubscriptionPlan = select.querySelector('option[value]:not([value=""])');

		if (!firstSubscriptionPlan) return;

		this.#changeSellingPlan({
			line: Number(wrapper.dataset.line),
			sellingPlan: firstSubscriptionPlan.value,
		});
	};


	#handleSubscriptionChange = (event) => {
		const select = event.target.closest('[data-cart-subscription-plans]');

		if (!select) return;

		const line = Number(select.dataset.line);

		this.#changeSellingPlan({
			line,
			sellingPlan: select.value || null,
		});
	};


	#changeSellingPlan({ line, sellingPlan }) {

		const sectionsToUpdate = new Set([this.sectionId]);

		const cartItemsComponents = document.querySelectorAll('cart-items-component');

		cartItemsComponents.forEach((item) => {
			if (item.dataset.sectionId) {
				sectionsToUpdate.add(item.dataset.sectionId);
			}
		});


		this.#disableCartItems();


		const body = JSON.stringify({
			line,
			quantity: this.refs.cartItemRows[line - 1]?.dataset.quantity || 1,
			selling_plan: sellingPlan,
			sections: Array.from(sectionsToUpdate).join(','),
			sections_url: window.location.pathname,
		});


		fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
			.then((response) => response.json())
			.then((response) => {

				if (response.errors) {
					console.error(response.errors);
					return;
				}


				this.dispatchEvent(
					new CartUpdateEvent({}, this.sectionId, {
						itemCount: response.item_count,
						source: 'subscription-update',
						sections: response.sections,
					})
				);


				morphSection(
					this.sectionId,
					response.sections[this.sectionId]
				);

			})
			.catch(console.error)
			.finally(() => {
				this.#enableCartItems();
			});
	}

	/**
	 * Handles the line item removal.
	 * @param {number} line - The line item index.
	 */
	async onLineItemRemove(line) {
		const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

		if (!cartItemRowToRemove) return;

		// Figure out the linked line (if any) BEFORE removing anything - doing
		// both removals in one request avoids the line-index-shifts-after-
		// removal race that two separate calls would hit.
		const linked = await this.#findLinkedLine(line);

		if (linked) {
			this.#applyLineQuantities(linked.items, { [line]: 0, [linked.line]: 0 });
		} else {
			this.updateQuantity({ line, quantity: 0, action: 'clear' });
		}

		const linkedRow = linked ? this.refs.cartItemRows[linked.line - 1] : null;

		const rowsToRemove = [
			cartItemRowToRemove,
			...(linkedRow ? [linkedRow] : []),
			// Get all nested lines of the row to remove
			...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
		];

		// Add class to the row to trigger the animation
		rowsToRemove.forEach((row) => {
			const remove = () => row.remove();

			if (prefersReducedMotion()) return remove();

			row.style.setProperty('--row-height', `${row.clientHeight}px`);
			row.classList.add('removing');

			// Remove the row after the animation ends
			onAnimationEnd(row, remove);
		});
	}

	/**
	 * Looks up whether `line` carries a hidden `_bundle_id` property (never
	 * reaches the DOM - leading "_" - so this has to go through /cart.js)
	 * and, if so, finds the other cart line sharing that id (e.g. a bundle's
	 * linked subscription line).
	 * @param {number} line - 1-indexed line number.
	 * @returns {Promise<{ items: Array<{quantity: number}>, line: number } | null>}
	 */
	async #findLinkedLine(line) {
		const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
		const bundleId = cart?.items?.[line - 1]?.properties?._bundle_id;
		if (!cart || !bundleId) return null;

		const linkedIndex = cart.items.findIndex(
			(item, i) => i !== line - 1 && item.properties?._bundle_id === bundleId
		);

		return linkedIndex === -1 ? null : { items: cart.items, line: linkedIndex + 1 };
	}

	/**
	 * Applies quantities to specific lines in one atomic /cart/update.js call
	 * (array form: one quantity per line, in order) - used for both removing
	 * linked lines together (quantity 0) and syncing their quantity together.
	 * @param {Array<{quantity: number}>} items - cart.items from /cart.js.
	 * @param {Record<number, number>} lineQuantities - 1-indexed line -> new quantity.
	 */
	#applyLineQuantities(items, lineQuantities) {
		this.#disableCartItems();
		this.refs.cartTotal?.shimmer();

		const sectionsToUpdate = new Set([this.sectionId]);
		document.querySelectorAll('cart-items-component').forEach((el) => {
			if (el instanceof HTMLElement && el.dataset.sectionId) sectionsToUpdate.add(el.dataset.sectionId);
		});

		const body = JSON.stringify({
			updates: items.map((item, i) => lineQuantities[i + 1] ?? item.quantity),
			sections: Array.from(sectionsToUpdate).join(','),
			sections_url: window.location.pathname,
		});

		fetch(Theme.routes.cart_update_url || '/cart/update.js', fetchConfig('json', { body }))
			.then((response) => response.json())
			.then((parsed) => {
				resetShimmer(this);
				if (parsed.errors) return console.error(parsed.errors);

				this.#updateQuantitySelectors(parsed);
				this.dispatchEvent(
					new CartUpdateEvent({}, this.sectionId, {
						itemCount: parsed.item_count,
						source: 'cart-items-component',
						sections: parsed.sections,
					})
				);
				morphSection(this.sectionId, parsed.sections[this.sectionId]);
				this.#updateCartQuantitySelectorButtonStates();
			})
			.catch((error) => console.error(error))
			.finally(() => this.#enableCartItems());
	}

	/**
	 * Updates the quantity.
	 * @param {Object} config - The config.
	 * @param {number} config.line - The line.
	 * @param {number} config.quantity - The quantity.
	 * @param {string} config.action - The action.
	 */
	updateQuantity(config) {
		const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

		this.#disableCartItems();

		const { line, quantity } = config;
		const { cartTotal } = this.refs;

		const cartItemsComponents = document.querySelectorAll('cart-items-component');
		const sectionsToUpdate = new Set([this.sectionId]);
		cartItemsComponents.forEach((item) => {
			if (item instanceof HTMLElement && item.dataset.sectionId) {
				sectionsToUpdate.add(item.dataset.sectionId);
			}
		});

		const body = JSON.stringify({
			line: line,
			quantity: quantity,
			sections: Array.from(sectionsToUpdate).join(','),
			sections_url: window.location.pathname,
		});

		cartTotal?.shimmer();

		fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
			.then((response) => {
				return response.text();
			})
			.then((responseText) => {
				const parsedResponseText = JSON.parse(responseText);

				resetShimmer(this);

				if (parsedResponseText.errors) {
					this.#handleCartError(line, parsedResponseText);
					return;
				}

				const newSectionHTML = new DOMParser().parseFromString(
					parsedResponseText.sections[this.sectionId],
					'text/html'
				);

				// Grab the new cart item count from a hidden element
				const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
				const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

				// Update data-cart-quantity for all matching variants
				this.#updateQuantitySelectors(parsedResponseText);

				this.dispatchEvent(
					new CartUpdateEvent({}, this.sectionId, {
						itemCount: newCartItemCount,
						source: 'cart-items-component',
						sections: parsedResponseText.sections,
					})
				);

				morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

				this.#updateCartQuantitySelectorButtonStates();
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				this.#enableCartItems();
				cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
			});
	}

	/**
	 * Handles the discount update.
	 * @param {DiscountUpdateEvent} event - The event.
	 */
	handleDiscountUpdate = (event) => {
		this.#handleCartUpdate(event);
	};

	/**
	 * Handles the cart error.
	 * @param {number} line - The line.
	 * @param {Object} parsedResponseText - The parsed response text.
	 * @param {string} parsedResponseText.errors - The errors.
	 */
	#handleCartError = (line, parsedResponseText) => {
		const quantitySelector = this.refs.quantitySelectors[line - 1];
		const quantityInput = quantitySelector?.querySelector('input');

		if (!quantityInput) throw new Error('Quantity input not found');

		quantityInput.value = quantityInput.defaultValue;

		const cartItemError = this.refs[`cartItemError-${line}`];
		const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

		if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
		if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

		cartItemError.textContent = parsedResponseText.errors;
		cartItemErrorContainer.classList.remove('hidden');
	};

	/**
	 * Handles the cart update.
	 *
	 * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
	 */
	#handleCartUpdate = (event) => {
		if (event instanceof DiscountUpdateEvent) {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
			return;
		}
		if (event.target === this) return;

		const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
		if (cartItemsHtml) {
			morphSection(this.sectionId, cartItemsHtml);

			// Update button states for all cart quantity selectors after morph
			this.#updateCartQuantitySelectorButtonStates();
		} else {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
		}
	};

	/**
	 * Disables the cart items.
	 */
	#disableCartItems() {
		this.classList.add('cart-items-disabled');
	}

	/**
	 * Enables the cart items.
	 */
	#enableCartItems() {
		this.classList.remove('cart-items-disabled');
	}

	/**
	 * Updates quantity selectors for all matching variants in the cart.
	 * @param {Object} updatedCart - The updated cart object.
	 * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
	 */
	#updateQuantitySelectors(updatedCart) {
		if (!updatedCart.items) return;

		for (const item of updatedCart.items) {
			const variantId = item.variant_id.toString();
			const selectors = document.querySelectorAll(
				`quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
			);

			for (const selector of selectors) {
				const input = selector.querySelector('input[data-cart-quantity]');
				if (!input) continue;

				input.setAttribute('data-cart-quantity', item.quantity.toString());

				// Update the quantity selector's internal state
				if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
					selector.updateCartQuantity();
				}
			}
		}
	}

	/**
	 * Updates button states for all cart quantity selector components.
	 */
	#updateCartQuantitySelectorButtonStates() {
		const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
		for (const selector of cartQuantitySelectors) {
			if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
				selector.updateButtonStates();
			}
		}
	}

	/**
	 * Gets the section id.
	 * @returns {string} The section id.
	 */
	get sectionId() {
		const { sectionId } = this.dataset;

		if (!sectionId) throw new Error('Section id missing');

		return sectionId;
	}
}

if (!customElements.get('cart-items-component')) {
	customElements.define('cart-items-component', CartItemsComponent);
}